import {
  Range,
  Root,
  SliderProps as SliderRootProps,
  Thumb,
  Track,
} from '@radix-ui/react-slider';
import clsx from 'clsx';
import { useId } from 'react';
import { useControllableState } from '../utils/use-controllable-state';
import {
  slider,
  sliderContainer,
  sliderFieldLabel,
  sliderLabel,
  sliderRange,
  sliderThumb,
  sliderTrack,
  valueLabel,
} from './slider.css';

export type SliderProps = {
  withLabels?: boolean;
  suffix?: string;
  className?: string;
  label?: string;
  containerClassName?: string;

  defaultValue?: SliderRootProps['defaultValue'];
  value?: SliderRootProps['value'];
  onValueChange?: SliderRootProps['onValueChange'];
  onValueCommit?: SliderRootProps['onValueCommit'];
  min?: SliderRootProps['min'];
  max?: SliderRootProps['max'];
  step?: SliderRootProps['step'];
  disabled?: boolean;
};

export const Slider = function SliderComponent({
  ref,
  className,
  value,
  defaultValue,
  onValueChange,
  onValueCommit,
  min = 0,
  max = 100,
  withLabels = true,
  suffix = '',
  step,
  disabled,
  containerClassName,
  label,
}: SliderProps & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  const reactId = useId();
  const inputId = reactId;
  const [sliderValue, setSliderValue] = useControllableState({
    value,
    defaultValue,
    onChange: onValueChange,
  });

  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={inputId} className={sliderFieldLabel}>
          {label}
        </label>
      )}
      <div ref={ref} className={clsx(sliderContainer, className)}>
        {withLabels ? (
          <div className={sliderLabel}>
            <span aria-hidden="true">{`${min}${suffix}`}</span>
            <span aria-hidden="true">{`${max}${suffix}`}</span>
          </div>
        ) : null}

        <Root
          className={slider}
          value={sliderValue}
          onValueChange={setSliderValue}
          onValueCommit={onValueCommit}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        >
          <Track className={sliderTrack}>
            <Range className={sliderRange} />
          </Track>

          <Thumb className={sliderThumb} id={inputId}>
            {sliderValue && withLabels ? (
              <span
                className={valueLabel}
                aria-hidden="true"
              >{`${sliderValue[0]}${suffix}`}</span>
            ) : null}
          </Thumb>
        </Root>
      </div>
    </div>
  );
};
